// v0.5.7 Stage 4 — Per-row resolver for the Art 54(2)(b)
// work-on-movable-property flag (ART54_2B_WORK_LOCATION_REQUIRED).
//
// This flag was previously unresolvable: the row needs the physical
// WORK LOCATION and nothing in the queue collected it. Mirrors the
// FxTreatmentAmend popover: the operator enters the service country +
// an evidence note; the backend records the country ON THE ROW (the
// input the Art 54(2)(b) rule reads) and the next re-run (one for every
// answer, SavedAnswersBar) routes the row LOCAL_SALE and drops the blocking flag.
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { resolveServiceCountry } from "../../lib/api";
import { errorMessage } from "../../lib/errorMessage";
import { Popover, PopoverContent, PopoverTrigger } from "../ui/popover";
import { toast } from "../ui/sonner";
import { PresetReasonSelect } from "../PresetReasonSelect";
import { SERVICE_COUNTRY_REASONS } from "../../lib/reviewReasonPresets";
import { ALL_COUNTRY_CHOICES, countryOptionLabel } from "../../lib/countries";

export const ServiceCountryResolver = ({ tid, calcId, calcFrozen, acked, onResolved, hideChip = false }) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [country, setCountry] = useState("");
  const [reason, setReason] = useState("");
  const [preset, setPreset] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  const reset = () => { setCountry(""); setReason(""); setPreset(""); setErr(null); };

  const cc = country.trim().toUpperCase();
  const cleaned = reason.trim();
  const valid = /^[A-Z]{2}$/.test(cc)
    && cleaned.length >= 8 && !/[<>]/.test(cleaned);

  if (acked) {
    return (
      <>
      {hideChip ? (
        <span data-testid={`rg-done-${tid}`} data-resolved="true" className="text-xs text-emerald-700 dark:text-emerald-300">
          ✓ {t("ui.review_grouped.row_resolved_label", { defaultValue: "Reviewed" })}
        </span>
      ) : (
      <span
        data-testid={`rg-txn-${tid}`}
        data-resolved="true"
        className="px-2 py-1 rounded border bg-emerald-50 border-emerald-200 text-emerald-700 dark:bg-emerald-950/40 dark:border-emerald-800 dark:text-emerald-200 truncate text-[10px] tabular-nums"
        title={tid}
      >
        ✓ {tid.slice(0, 12)}…
      </span>
      )}
      </>
    );
  }

  const submit = async () => {
    if (!valid || busy) return;
    setBusy(true); setErr(null);
    try {
      await resolveServiceCountry(calcId, {
        txn_id: tid, service_country: cc, reason: cleaned,
      });
      setOpen(false); reset();
      toast.success(
        t("ui.review_grouped.sc_toast_ok", { defaultValue: "Work country recorded — figures change at the next re-run" }),
        { description: `${tid.slice(0, 12)}… → ${cc}` },
      );
      // A1-105 — saved for the one re-run, like every Step 4 answer
      if (onResolved) await onResolved();
    } catch (e) {
      const msg = errorMessage(e);
      setErr(msg);
      toast.error(
        t("ui.review_grouped.sc_toast_fail", { defaultValue: "Could not set work country" }),
        { description: msg });
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
    <div className="flex items-center gap-2 flex-wrap">
      {!hideChip && (
      <span
        data-testid={`rg-txn-${tid}`}
        data-resolved="false"
        className="px-2 py-1 rounded border bg-slate-100 border-slate-200 text-slate-700 dark:bg-slate-800 dark:border-slate-700 dark:text-slate-200 truncate text-[10px] tabular-nums"
        title={tid}
      >
        {tid.slice(0, 12)}…
      </span>
      )}
      {!calcFrozen && (
        <Popover open={open} onOpenChange={(o) => { setOpen(o); if (!o) reset(); }}>
          <PopoverTrigger asChild>
            <button
              type="button"
              data-testid={`sc-resolve-open-${tid}`}
              className="text-[10px] px-2 py-1 rounded border border-red-300 dark:border-red-700 hover:bg-red-100 dark:hover:bg-red-900/40 text-red-700 dark:text-red-200"
            >
              {t("ui.review_grouped.sc_resolve_btn", { defaultValue: "Set work country →" })}
            </button>
          </PopoverTrigger>
          <PopoverContent
            data-testid={`sc-resolve-popover-${tid}`}
            className="w-80 bg-white dark:bg-slate-900 border border-slate-300 dark:border-slate-700 space-y-2"
          >
            <div className="text-[11px] font-bold text-slate-700 dark:text-slate-200">
              {t("ui.review_grouped.sc_resolve_title", { defaultValue: "Where was the work carried out?" })}
            </div>
            <div className="text-[10px] text-slate-500 dark:text-slate-400 leading-snug">
              {t("ui.review_grouped.sc_resolve_help")}
            </div>
            {/* v0.6.12 A5-27 — a country is picked, not typed. EU and UK
                first, then the rest of the world: the backend accepts any
                two-letter code, so the list is the full one. */}
            <select
              data-testid={`sc-resolve-country-${tid}`}
              value={country}
              onChange={(e) => setCountry(e.target.value)}
              aria-label={t("ui.review_grouped.sc_country_pick", { defaultValue: "Country where the work was done…" })}
              className="w-full bg-white dark:bg-slate-800 border border-slate-300 dark:border-slate-700 text-slate-900 dark:text-slate-100 rounded px-2 py-1 text-xs"
            >
              <option value="">
                {t("ui.review_grouped.sc_country_pick", { defaultValue: "Country where the work was done…" })}
              </option>
              {ALL_COUNTRY_CHOICES.map((c) => (
                <option key={c} value={c}>{countryOptionLabel(c)}</option>
              ))}
            </select>
            <PresetReasonSelect
              testid={`sc-resolve-reason-preset-${tid}`}
              presets={SERVICE_COUNTRY_REASONS}
              preset={preset}
              onPick={(p, v) => { setPreset(p); setReason(v); }}
            />
            {err && (
              <div data-testid={`sc-resolve-error-${tid}`} className="text-[10px] text-red-700 dark:text-red-300">
                {err}
              </div>
            )}
            <div className="flex justify-end gap-2">
              <button
                type="button"
                data-testid={`sc-resolve-cancel-${tid}`}
                onClick={() => { setOpen(false); reset(); }}
                className="text-[10px] underline text-slate-600 dark:text-slate-300"
              >
                {t("ui.review_grouped.sc_cancel", { defaultValue: "Cancel" })}
              </button>
              <button
                type="button"
                data-testid={`sc-resolve-submit-${tid}`}
                data-resolution-class="self-resolve"
                data-resolution-endpoint="POST /api/runs/calc/{calc_id}/resolve-service-country"
                disabled={busy || !valid}
                onClick={submit}
                className="text-[10px] font-bold px-3 py-1 rounded bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-white"
              >
                {busy
                  ? t("ui.review_grouped.sc_submitting", { defaultValue: "Saving…" })
                  : t("ui.review_grouped.sc_submit", { defaultValue: "Save" })}
              </button>
            </div>
          </PopoverContent>
        </Popover>
      )}
    </div>
    </>
  );
};

export default ServiceCountryResolver;
